import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import { AudienceTargetingGroup, FilterSelectOption, JapiQuery } from '../../../shared/_models/models';
import { ApiService } from '../../../shared/_services/api.service';
import { GtmService } from '../../../shared/_services/gtm.service';
import { SharedStoreService } from '../../../shared/_services/shared-store.service';
import { SharedService } from '../../../shared/_services/shared.service';
import * as AudienceTargetingActions from '../_store/audience-targeting.actions';
import * as fromAudienceTargeting from '../_store/audience-targeting.reducer';
import {
  audienceTargetingModelFiltersConfig,
  segmentsPartnerModelFiltersConfig,
  segmentsRxModelFiltersConfig,
} from './audience-targeting.config';

@Injectable({
  providedIn: 'root'
})
export class AudienceTargetingService {

  private getAudienceTargetingSegments$: Subscription;

  constructor(
    private api: ApiService,
    private gtmService: GtmService,
    private store: Store<fromAudienceTargeting.AudienceTargetingState>,
    private sharedService: SharedService,
    private sharedStoreService: SharedStoreService,
  ) {}

  getAudienceTargetingSegments(filters: JapiQuery): void {
    this.getAudienceTargetingSegments$?.unsubscribe();
    this.gtmService.customEvent('audienceTargetingFilters', filters as unknown as {[p: string]: string | number});
    // until there's a limit for the segment list, to prevent 9000+ values
    this.store.dispatch(new AudienceTargetingActions.FetchSegmentListRequestAction());
    this.getAudienceTargetingSegments$ = this.api.getAudienceTargetingSegments(filters)
      .subscribe(res => {
        this.store.dispatch(new AudienceTargetingActions.FetchSegmentListSuccessAction(
          {segments: res.data, totalElements: res.totalElements}
        ));
      },
      (err) => {
        this.store.dispatch(new AudienceTargetingActions.FetchSegmentListFailureAction(err));
        this.sharedService.onHttpCompletionResolve(false, 'Failure', 'Audience targeting segments fetch failed', '', '');
      });
  }

  getAudienceTargetingProviderList(rxDealMode: boolean = false): void {
    const getProviderRequest$ = rxDealMode ? this.api.getAudienceTargetingProviders() : this.api.getPubDealAudienceTargetingProviders();
    this.store.dispatch(new AudienceTargetingActions.FetchSegmentProviderListRequestAction());
    getProviderRequest$.pipe(
      map(audienceProviders => {
        const formattedAudienceProviders = Object.entries(audienceProviders).reduce((acc, [id, name]) => {
          acc[+id] = `${name} (${id})`;
          return acc;
        }, {});
        return this.sharedService.keyValueToFilterValue(formattedAudienceProviders, true);
      })
    ).subscribe((res: FilterSelectOption[]) => {
      const filters = [audienceTargetingModelFiltersConfig, segmentsRxModelFiltersConfig, segmentsPartnerModelFiltersConfig];

      filters.forEach(filter => {
        const providerFilter = filter.find(a => a.id === 'audienceTargetingModelProvider');
        if (providerFilter) {
          providerFilter.possibleValues = res;
        }
      });

      this.store.dispatch(new AudienceTargetingActions.FetchSegmentProviderListSuccessAction(
        {
          segmentProviders: res,
          updateAppFilters: this.sharedService.updateFilterPossibleValues
        }
      ));
    },
    (err) => {
      this.store.dispatch(new AudienceTargetingActions.FetchSegmentProviderListFailureAction(err));
      this.sharedService.onHttpCompletionResolve(false, 'Failure', 'Audience targeting provider list fetch failed', '', '');
    });
  }

  getAudienceTargetingSortByList(): void {
    this.store.dispatch(new AudienceTargetingActions.FetchSegmentSortByListRequestAction());
    this.sharedStoreService.getSharedEnum('SegmentSortedFields').pipe(
      map(res => this.sharedService.keyValueToFilterValue(res)),
      map(res => res.map(s => {
        s.displayName = 'Sort by ' + s.displayName;
        return s;
      }))
    )
      .subscribe((res: FilterSelectOption[]) => {
        const findSortByFilter = audienceTargetingModelFiltersConfig.find(a => a.id === 'audienceTargetingModelSortBy');
        findSortByFilter.possibleValues = res;
        this.store.dispatch(new AudienceTargetingActions.FetchSegmentSortByListSuccessAction(
          {
            segmentSortByList: res,
            updateAppFilters: this.sharedService.updateFilterPossibleValues
          }
        ));
      },
      (err) => {
        this.store.dispatch(new AudienceTargetingActions.FetchSegmentSortByListFailureAction(err));
        this.sharedService.onHttpCompletionResolve(false, 'Failure', 'Audience targeting sort by list fetch failed', '', '');
      });
  }

  gtmCloseEvent(closeMethod: string): void {
    this.gtmService.customEvent('audienceTargetingCloseEvent', {closeMethod});
  }
}
